import {Loader} from '@mantine/core';
import type {GetServerSideProps} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type {GetServerSidePropsResult} from 'next/types';
import React, {useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  type QuestionOrderType,
  questionOrderType,
} from '../../../core/entities/box/constants';
import {useInfiniteQuestions} from '../../../core/repositories/boxRepository';
import type {QuestionWithAnswersType} from '../../../core/repositories/boxRepositoryTypes';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
  getUsersMe,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import Layout from '../../../features/box/components/Layout';
import {Navigation} from '../../../features/box/components/Navigation';
import {QuestionFeedItem} from '../../../features/box/components/QuestionFeedItem';
import {SearchForm} from '../../../features/box/components/SearchForm';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  loggedinAdviserName?: string;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {},
      };
    }

    const usersMeResponse = await getUsersMe({cookies});
    if (!usersMeResponse.ok) {
      return {
        props: {},
      };
    }

    const props: Props = {
      loggedinAdviserName: usersMeResponse.value.adviserUserName,
    };

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const QuestionFeed: NextPageWithLayout<Props> = ({
  loggedinAdviserName,
  userDataForAnalytics,
}) => {
  const router = useRouter();
  /*
   * ホスト名がlocalhostになっているため、OGPやSEOのURLを指定する要素に使えない.
   * 一旦ベタ書きで対応し、後に修正する.
   * todo URLのベタ書きを環境変数等から取得するように修正する.
   */
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const [searchWords, setSearchWords] = useState<string>('');
  const [selectQuestionOrderType, setSelectQuestionOrderType] =
    useState<QuestionOrderType>(questionOrderType.answeredAt);
  const {ref, inView} = useInView();
  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuestions({
    limit: 10,
    q: searchWords,
    questionOrderType: selectQuestionOrderType,
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const answersListLastPosition = sessionStorage.getItem(
      `__next_scroll_${window.history.state.key}`,
    );
    if (answersListLastPosition) {
      const yPosition = Number(
        Object.values(JSON.parse(answersListLastPosition))[1],
      );
      window.scrollTo(0, yPosition);
    } else {
      const yPosition = 0;
      window.scrollTo(0, yPosition);
    }

    void refetch();
  }, [refetch, searchWords, questionOrderType]);

  return (
    <>
      <Head>
        <meta key="og:url" property="og:url" content={currentUrl} />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>
      <div className="flex min-h-screen items-start justify-center bg-background-1 pb-40 pc:pt-32">
        <div className="w-full max-w-[800px] bg-white py-16 pc:px-16">
          <div className="px-16 pc:px-0">
            <SearchForm
              placeholder="過去の質問・回答を検索"
              setSearchWords={setSearchWords}
              setQuestionOrderType={setSelectQuestionOrderType}
            />
            <h1 className="mb-8 mt-24 text-lg font-bold leading text-black-1 pc:text-md">
              {searchWords === '' ? '最新の質問' : `${searchWords}の検索結果`}
            </h1>
          </div>
          {data?.pages &&
            data.pages.length > 0 &&
            data.pages.flatMap((page, i) => {
              return (
                page.ok && (
                  <React.Fragment key={`page-${i}`}>
                    {page.value.questions.map((question) => (
                      <div ref={ref} key={keyof(question)}>
                        <QuestionFeedItem question={question} />
                      </div>
                    ))}
                  </React.Fragment>
                )
              );
            })}
          {!isFetching && (!data || data.pages.length <= 0) && (
            <div className=" px-16 py-32">
              <p className=" text-center text-md leading text-black-1">
                検索結果は見つかりませんでした
              </p>
            </div>
          )}
          {(isFetching || isFetchingNextPage) && (
            <div className="flex justify-center py-24">
              <Loader color="gray" size="lg" />
            </div>
          )}
        </div>
      </div>

      <Navigation loggedinAdviserName={loggedinAdviserName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default QuestionFeed;

QuestionFeed.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

function keyof(question: QuestionWithAnswersType) {
  return `${question.id}-${question.answers[0]?.id}`;
}
